import { AddressValue } from './Locations';

export interface LocationSuggestion {
  all: AddressValue[];
  establishments: AddressValue[];
  cities: AddressValue[];
  states: AddressValue[];
  countries: AddressValue[];
}

type ParseStateAndCountry = {
  city: string | undefined;
  state: string | undefined;
  country: string | undefined;
};

export type DisabledSuggestions = {
  [x: string]: boolean;
};

export const getDisabledSuggestions = (
  disabledSuggestions: DisabledSuggestions,
  address: AddressValue,
): DisabledSuggestions => {
  if (!address.address) return disabledSuggestions;
  const {
    value: { city, state, country },
    length,
  } = parseStateAndCountry(address.address);
  switch (length) {
    case 1:
    case 0:
      break;
    case 2:
      country && (disabledSuggestions[country] = true);
      break;
    case 3:
      state && (disabledSuggestions[state] = true);
      country && (disabledSuggestions[country] = true);
      break;
    default:
      city && (disabledSuggestions[city] = true);
      country && (disabledSuggestions[country] = true);
      state && (disabledSuggestions[state] = true);
      break;
  }
  return disabledSuggestions;
};

export const getAllDisabledSuggestions = (
  selected: AddressValue[],
  skipIndex: number,
): DisabledSuggestions => {
  const disabled: DisabledSuggestions = {};
  selected.forEach((s, i) => {
    if (i === skipIndex) return;
    getDisabledSuggestions(disabled, s);
  });
  return disabled;
};

const parseStateAndCountry = (
  value: string | undefined,
): { value: ParseStateAndCountry; length: number } => {
  const result: ParseStateAndCountry = {
    city: undefined,
    state: undefined,
    country: undefined,
  };
  if (!value) return { value: result, length: 0 };
  const split = value.split(', ');
  const length = split.length;
  if (length === 1) {
    result.country = split[0];
  } else if (length === 2) {
    result.state = value;
    result.country = split[1];
  } else if (length > 2) {
    const last = split.length - 1;
    result.city = split[last - 2] + ', ' + split[last - 1] + ', ' + split[last];
    result.state = split[last - 1] + ', ' + split[last];
    result.country = split[last];
  }
  return { value: result, length };
};

const shouldAdd = (
  value: string | undefined,
  array: AddressValue[],
): boolean => {
  if (!value) return false;
  return array.findIndex(x => x.address === value) === -1;
};

const arrayContains = (
  array: string[],
  logic: (val: string) => boolean,
): boolean => {
  return array.findIndex(logic) !== -1;
};

export const parseGoogleSuggestions = (
  suggestions: google.maps.places.AutocompletePrediction[],
): LocationSuggestion => {
  const newSuggestions: LocationSuggestion = {
    all: [],
    establishments: [],
    cities: [],
    states: [],
    countries: [],
  };
  suggestions.forEach(s => {
    newSuggestions.all.push({
      address: s.description,
      type: 'all',
      place_id: s.place_id,
    });
    if (s.types.includes('country')) {
      newSuggestions.countries.push({
        address: s.description,
        type: 'countries',
        place_id: s.place_id,
      });
    } else if (
      arrayContains(
        s.types,
        x => x === 'establishment' || x === 'street_address',
      )
    ) {
      const {
        value: { city, state, country },
      } = parseStateAndCountry(s['structured_formatting']['secondary_text']);
      newSuggestions.establishments.push({
        address: s.description,
        type: 'establishments',
        place_id: s.place_id,
      });
      shouldAdd(city, newSuggestions.cities) &&
        newSuggestions.cities.push({
          address: city,
          type: 'cities',
        });
      shouldAdd(state, newSuggestions.states) &&
        newSuggestions.states.push({
          address: state,
          type: 'states',
        });
      shouldAdd(country, newSuggestions.countries) &&
        newSuggestions.countries.push({
          address: country,
          type: 'countries',
        });
    } else if (s.types.includes('administrative_area_level_1')) {
      newSuggestions.states.push({
        address: s.description,
        type: 'states',
      });
    } else {
      newSuggestions.cities.push({
        address: s.description,
        type: 'cities',
      });
      const {
        value: { state, country },
      } = parseStateAndCountry(s['structured_formatting']['secondary_text']);
      shouldAdd(state, newSuggestions.states) &&
        newSuggestions.states.push({
          address: state,
          type: 'states',
        });
      shouldAdd(country, newSuggestions.countries) &&
        newSuggestions.countries.push({
          address: country,
          type: 'countries',
        });
    }
  });
  return newSuggestions;
};

export const getTotalPercentage = (addresses: AddressValue[] | undefined | null): number => {
  if (!addresses) return 0;
  let totalPercentage = 0;
  for (let i = 0; i < addresses.length; i++) {
    const element = addresses[i];
    if (!element.percentage) return 0; // make sure all the fields are filled
    totalPercentage += element.percentage;
  }
  return totalPercentage;
};
